import React, { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Box, Heading } from '@rover/kibble/core';
import { CountryConfigurationDogSizesItem } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationDogSizesItem';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { ProviderProfileHostingServicePreferences } from '@rover/rsdk/src/apiClient/latest/model/providerProfileHostingServicePreferences';
import { ProviderProfileTravelingServicePreferencesProperty } from '@rover/rsdk/src/apiClient/latest/model/providerProfileTravelingServicePreferencesProperty';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';

import { SitterProfileVersionOptions } from '../../constants';
import { useFilteredAttributesByPetTypes } from '../../hooks/useFilteredAttributesByPetTypes';
import { SitterProfileVersion } from '../../hooks/useRevampProfileContext';
import { isLegacyVersion } from '../../utils/utils';

import PetPreferences from './PetPreferences';
import PetSizes from './PetSizes';

type Preferences =
  | ProviderProfileHostingServicePreferences
  | ProviderProfileTravelingServicePreferencesProperty;

export enum petPreferencesTypes {
  HOSTING = 'hosting',
  TRAVELING = 'traveling',
  GROOMING = 'grooming',
  DOG_TRAINING = 'dog_training',
}

const ServicePreferencesSection = ({
  sitterName,
  preferencesType,
  weightLabel,
  dogSizes,
  preferences: originalPreferences,
  activeServices,
  sitterProfileVersion = SitterProfileVersionOptions.LEGACY,
}: {
  sitterName: string;
  preferences: Preferences;
  preferencesType: petPreferencesTypes.HOSTING | petPreferencesTypes.TRAVELING;
  weightLabel?: string;
  dogSizes?: CountryConfigurationDogSizesItem[];
  activeServices: EnhancedPersonSitterProfileService[];
  sitterProfileVersion?: SitterProfileVersion;
}): JSX.Element => {
  const { t } = useLingui();
  const { current: countryConfiguration } = useCountryConfigurationShim();
  const { filterAttributesByPetTypes } = useFilteredAttributesByPetTypes();

  const preferences = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(originalPreferences || {}).map(([key, value]) => [
          key,
          Array.isArray(value) ? filterAttributesByPetTypes(value) : value,
        ])
      ),
    [originalPreferences, filterAttributesByPetTypes]
  );

  const getPreferencesTitle = (): string | undefined => {
    if (preferencesType === petPreferencesTypes.HOSTING) {
      return t`${sitterName} can host`;
    }
    return t`${sitterName} can care for`;
  };

  const acceptsOnlyOneClient = preferences?.dogsExperienceAttributes.find(
    (exp) => exp.key === 'accepts-only-one-client'
  );
  const attributes = [
    ...preferences!.dogsPreferencesAttributes,
    ...(acceptsOnlyOneClient ? [acceptsOnlyOneClient] : []),
  ];

  const acceptsCats =
    !!preferences?.acceptedPetsAttributes.find((attribute) => attribute.key === 'cat') &&
    !!activeServices.find(
      ({ serviceType }) =>
        serviceType.slug === ServiceType.DROP_IN ||
        serviceType.slug === ServiceType.OVERNIGHT_TRAVELING ||
        serviceType.slug === ServiceType.OVERNIGHT_BOARDING
    );

  const activeSizes = preferences?.acceptedDogsAttributes
    .filter((dogAttribute) => dogAttribute.active)
    .map((dogAttribute) => dogAttribute.key);

  const acceptedSizes = {
    small: activeSizes && activeSizes.includes('small'),
    medium: activeSizes && activeSizes.includes('medium'),
    large: activeSizes && activeSizes.includes('large'),
    giant: activeSizes && activeSizes.includes('giant'),
  };

  const isLegacySitterProfile = isLegacyVersion(sitterProfileVersion);

  const acceptedPetsAttributes = preferences?.acceptedPetsAttributes;
  const allAttributes = [...acceptedPetsAttributes, ...attributes];
  const shouldShowSectionTitle =
    isLegacySitterProfile && allAttributes.some((attribute) => !attribute.unrelatedToPetTypes);

  if (!preferences || !allAttributes.length) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Box pt={isLegacySitterProfile ? '8x' : '0x'} data-testid="service-preferences-section">
      {shouldShowSectionTitle && (
        <Heading webProps={{ as: 'h3' }} size="200" testID="service-detail-pet-preferences">
          {getPreferencesTitle()}
        </Heading>
      )}
      <Box>
        {acceptedPetsAttributes && (
          <PetSizes
            petsAttributes={acceptedPetsAttributes}
            dogSizes={dogSizes || countryConfiguration?.dogSizes}
            weightLabel={weightLabel || countryConfiguration?.weightUnit}
            cat={acceptsCats}
            {...acceptedSizes}
          />
        )}
        {attributes && (
          <PetPreferences
            dogsPreferencesAttributes={attributes}
            sitterProfileVersion={sitterProfileVersion}
          />
        )}
      </Box>
    </Box>
  );
};

export default ServicePreferencesSection;
